import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import * as d3 from "d3";
import { CrudService } from "src/app/Services/crud.service";
import { HierarchyNode, HierarchyLink } from "d3-hierarchy";

export interface CollapsibleNode extends HierarchyNode<any> {
  x0?: number;
  y0?: number;
  _children?: CollapsibleNode[] | null;
  id?: string;
}
@Component({
  selector: "visited-pages",
  templateUrl: "./visited-pages.component.html",
  standalone: false,
  styles: [
    `
      ::ng-deep.tree-container {
        width: 100%;
        height: 600px; /* viewport height */
        overflow: auto; /* enable scroll if SVG is larger */
      }

      body {
        overflow: hidden;
      }
    `,
  ],
})
export class VisitedPagesComponent implements OnInit, AfterViewInit {
  @ViewChild("treeSvg", { static: true }) treeSvg!: ElementRef<SVGSVGElement>;
  @Input("projectDetails") projectDetails;
  constructor(private http: CrudService, private route: ActivatedRoute) {}
  ngAfterViewInit() {
    this.http.get(
      null,
      (data) => {
        this.renderTree(data);
      },
      `usageAnalytics/get-user-tree?filter[where][projectManagementId]=" +
        ${this.projectId}`
    );
  }
  projectId;
  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.projectId = this.projectDetails.id ?? params.get("pid")!;
    });
  }

  renderTree(data: any) {
    const element = this.treeSvg.nativeElement;

    d3.select(element).selectAll("*").remove(); // clear previous tree

    // --- Build hierarchy ---
    const root: CollapsibleNode = d3.hierarchy(data) as CollapsibleNode;

    root.descendants().forEach((d: CollapsibleNode, i) => {
      d.id = String(i); // must be string
      d._children = d.children; // store children for collapse/expand
      if (d.depth && d.data.name.length !== 7) d.children = null; // initially collapsed
    });

    const width = 800;
    const nodeSpacing = 40;
    const height = (root.descendants().length + 1) * nodeSpacing;

    const svg = d3
      .select(element)
      .attr("width", width)
      .attr("height", height)
      .attr("style", "font: 10px sans-serif; user-select: none;");

    const g = svg.append("g").attr("transform", "translate(40,0)"); // margin left

    const treeLayout = d3
      .tree<CollapsibleNode>()
      .size([height, width - 100])
      .separation((a, b) => (a.parent === b.parent ? 1.5 : 2));

    const diagonal = d3
      .linkHorizontal()
      .x((d: any) => d.y)
      .y((d: any) => d.x);

    // --- Update function ---
    const update = (source: CollapsibleNode) => {
      treeLayout(root);

      // --- Links ---
      const links = root.links();
      const link = g
        .selectAll<SVGPathElement, d3.HierarchyLink<CollapsibleNode>>(".link")
        .data(links, (d) => d.target.id!);

      link
        .enter()
        .append("path")
        .attr("class", "link")
        .attr("fill", "none")
        .attr("stroke", "#555")
        .attr("stroke-width", 1.5)
        .merge(link)
        .transition()
        .duration(250)
        .attr("d", (d) => diagonal(d as any)); // cast to any to satisfy TS

      link.exit().remove();

      // --- Nodes ---
      const node = g
        .selectAll<SVGGElement, CollapsibleNode>(".node")
        .data(root.descendants(), (d) => d.id!);

      const nodeEnter = node
        .enter()
        .append("g")
        .attr("class", "node")
        .attr(
          "transform",
          (d) => `translate(${source.y0 ?? 0},${source.x0 ?? 0})`
        )
        .on("click", (event, d) => {
          d.children = d.children ? null : d._children; // toggle collapse
          update(d);
        });

      nodeEnter
        .append("circle")
        .attr("r", 6)
        .attr("fill", (d) => (d._children ? "#555" : "#69b3a2"));

      var nodeEnterText = nodeEnter
        .append("text")
        .attr("dy", 0) // will use tspan for vertical spacing
        .attr("x", (d) => (d.children || d._children ? -12 : 12))
        .style("text-anchor", (d) =>
          d.children || d._children ? "end" : "start"
        )
        .text((d) => `${d.data.name} (${d.data.count})`);

      // Wrap text to max width, e.g., 80px
      this.wrapText(nodeEnterText, 80);
      nodeEnter
        .merge(node)
        .transition()
        .duration(250)
        .attr("transform", (d) => `translate(${d.y},${d.x})`);

      node.exit().remove();

      // Save positions
      root.each((d: CollapsibleNode) => {
        d.x0 = d.x;
        d.y0 = d.y;
      });
    };

    root.x0 = height / 2;
    root.y0 = 0;

    update(root);
  }

  wrapText(
    textSelection: d3.Selection<SVGTextElement, any, SVGGElement, any>,
    width: number
  ) {
    textSelection.each(function () {
      const text = d3.select(this);
      const words = text.text().split(/\s+/).reverse();
      let word;
      let line: string[] = [];
      let lineNumber = 0;
      const lineHeight = 12; // px
      const y = text.attr("y") || 0;
      const x = +text.attr("x") || 0;
      let tspan = text.text(null).append("tspan").attr("x", x).attr("y", y);

      while ((word = words.pop())) {
        line.push(word);
        tspan.text(line.join(" "));
        if ((tspan.node() as SVGTextElement).getComputedTextLength() > width) {
          line.pop();
          tspan.text(line.join(" "));
          line = [word];
          tspan = text
            .append("tspan")
            .attr("x", x)
            .attr("y", y)
            .attr("dy", `${++lineNumber * lineHeight}px`)
            .text(word);
        }
      }
    });
  }
}
